import React, { useContext, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { toast, Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FiUploadCloud, FiFolder, FiFile } from "react-icons/fi";
import { BiCategory } from "react-icons/bi";
import { motion } from "framer-motion";

const BulkUploadForm = () => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadType, setUploadType] = useState("files");
  const { authState } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || files.length === 0) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append("contracts", file));
    formData.append("category", category);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/submit-bulk-contracts`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-auth-token": authState.token,
          },
        }
      );
      toast.success(response.data.message);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl p-8 bg-gray-900 rounded-xl shadow-2xl"
      >
        <h2 className="text-4xl font-extrabold text-center mb-8 text-purple-400">Bulk Contract Upload</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <BiCategory className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Category"
              className="w-full pl-12 pr-4 py-4 bg-gray-800 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition duration-200 text-lg"
            />
          </div>
          <div className="relative">
            <select
              value={uploadType}
              onChange={(e) => {
                setUploadType(e.target.value);
                setFiles([]);
              }}
              className="w-full pl-4 pr-10 py-4 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition duration-200 appearance-none text-lg"
            >
              <option value="files">Select Files</option>
              <option value="folder">Select Folder</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg className="fill-current h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
          <div className="relative">
            <label className="flex items-center justify-center w-full px-4 py-4 bg-gray-800 text-white rounded-lg cursor-pointer hover:bg-gray-700 transition duration-200 text-lg">
              {uploadType === "files" ? <FiFile className="mr-2 text-xl" /> : <FiFolder className="mr-2 text-xl" />}
              <span>{uploadType === "files" ? "Choose Files" : "Choose Folder"}</span>
              <input
                type="file"
                multiple={uploadType === "files"}
                webkitdirectory={uploadType === "folder" ? "true" : undefined}
                onChange={(e) => setFiles(Array.from(e.target.files))}
                className="hidden"
              />
            </label>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            className="w-full py-4 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold shadow-lg hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition duration-200 flex items-center justify-center text-lg"
            disabled={loading}
          >
            <FiUploadCloud className="mr-2 text-xl" />
            <span>{loading ? "Uploading..." : "Upload Contracts"}</span>
          </motion.button>
        </form>
        {files.length > 0 && (
          <div className="mt-6 p-4 bg-gray-800 rounded-lg">
            <p className="text-lg text-gray-400 mb-2">{files.length} file(s) selected</p>
            <ul className="text-sm text-gray-500 max-h-40 overflow-y-auto">
              {files.map((file, index) => (
                <li key={index} className="truncate py-1">{file.name}</li>
              ))}
            </ul>
          </div>
        )}
        <Toaster position="bottom-center" />
      </motion.div>
    </div>
  );
};

export default BulkUploadForm;